
import { VDPopupListboxClass } from "@/components/pickers/VDPopupListbox";
import VDPopupListbox from "@/components/pickers/VDPopupListbox.vue";
import {
  RoleAssigneeType,
  UserRoleType,
  useGetRolesForUserQuery,
  useGetOrganizationQuery,
  Maybe,
  RoleAssigneePartsFragment,
} from "@/graphql/types";
import GroupedItems from "@/models/GroupedItems";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { defineComponent, PropType, Ref, ref, watch, computed } from "vue";

gql`
  query getRolesForUser($id: ID) {
    rolesForUser(userId: $id) {
      ...RoleAssigneeParts
    }
  }
`;

export default defineComponent({
  components: {
    VDPopupListbox,
  },
  emits: ["addedRole"],
  props: {
    addedRoles: {
      type: Array as PropType<UserRoleType[]>,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: String,
      default: null,
    },
    organizationId: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    // VDOM
    const listbox = ref<VDPopupListboxClass>();

    // Refs
    const isEnabled = ref(true); // Set true to enable, so that the query runs immediately when open a user, so click on "Add roles" will show roles immediately

    // Data
    const getRolesResponse = useGetRolesForUserQuery(
      () => ({ id: props.userId }),
      () => ({
        enabled:
          isEnabled.value && props.userId !== null && props.userId !== "new",
        fetchPolicy: "network-only",
      })
    );

    //Computed
    const loading = computed(() => {
      return getRolesResponse.loading.value;
    });

    const error = computed(() => {
      return getRolesResponse.error.value;
    });

    // For new users, fetch organization and use its assigneeRoles; for existing users, use the query result
    const {
      result: orgResult,
      loading: orgLoading,
      error: orgError,
    } = useGetOrganizationQuery(
      () => ({
        organizationId: props.organizationId,
        pageParams: { pageSize: 10, currentPage: 1 },
      }),
      () => ({
        enabled: !props.userId || props.userId === "new" || props.userId === "",
      })
    );

    const rolesData = computed(() => {
      if (!props.userId || props.userId === "new" || props.userId === "") {
        return useResult(orgResult).value?.assigneeRoles || [];
      } else {
        return useResult(getRolesResponse.result).value || [];
      }
    });

    return {
      roles: useGroupedRolesByApplication(
        rolesData,
        props.organizationId,
        props.userId === "new" || props.userId === null // isNewUser - don't filter organizationRoles
      ),
      listbox,
      loading,
      error,
      toggle: (event: Event) => {
        isEnabled.value = true;
        listbox.value?.toggle(event);
      },
    };
  },
});

function useGroupedRolesByApplication(
  rolesData: Ref<
    RoleAssigneeType[] | readonly Maybe<RoleAssigneePartsFragment>[]
  >,
  organizationId: string,
  isNewUser: boolean
) {
  const groupedRoles = ref<GroupedItems<RoleAssigneeType>[]>([]);

  watch(
    rolesData,
    (newValue) => {
      const map: { [name: string]: RoleAssigneeType[] } = {};

      newValue?.forEach((element) => {
        if (element?.applicationName && map[element.applicationName]) {
          map[element.applicationName].push(element as RoleAssigneeType);
        } else if (element?.applicationName) {
          map[element.applicationName] = [element as RoleAssigneeType];
        }
      });

      const c = new Intl.Collator("da", { sensitivity: "base", numeric: true });
      const orgId = (!isNewUser && organizationId) || null;

      groupedRoles.value = Object.keys(map)
        .sort(c.compare)
        .map((app) => ({
          name: app,
          items: (orgId
            ? map[app].filter((x) => x.organizationId === orgId)
            : map[app]
          ).sort((a, b) => c.compare(a?.roleName ?? "", b?.roleName ?? "")),
        }))
        .filter((g) => g.items.length);
    },
    { deep: true }
  );

  return groupedRoles;
}
